import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { guardLoginUrl } from '../modules/auth/sessionSignals';
import { isOwnerGroup } from '../utils/rankPolicy';
import { maySeeSurface, desksForSurface, ANY_DESK, AUTHORITY_ONLY, SUPER_ONLY } from '@moksy/common/src/permissions/desks.js';

export const AdminRoute = ({ children }) => {
  const { user, activeRole, availableRoles, loading, isAuthenticated, personaSettled } = useAuthContext();
  const location = useLocation();

  // 🎭 The stored hat is read from localStorage at mount and may belong to the
  // PREVIOUS account on this browser; `useAuth` drops it, but not before this
  // guard's first render. Refusing in that window bounced a super admin off
  // their own console with "you are viewing as a member" (walked 2026-09-09).
  // Waiting costs a spinner frame; not waiting costs the page.
  if (loading || (isAuthenticated && !personaSettled)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-gray-400 font-medium">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Somebody who just signed out is told so, not asked to log in as if
    // refused (GC-092) — guardLoginUrl reads the sign-out signal.
    const next = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
    return <Navigate to={guardLoginUrl({ next })} replace />;
  }

  const primaryRole = String(user?.role || '').toLowerCase();
  const safeActiveRole = Array.isArray(availableRoles) && availableRoles.includes(activeRole)
    ? String(activeRole || '').toLowerCase()
    : primaryRole;
  const effectiveRole = safeActiveRole || primaryRole;

  // Owner-group covers super_admin, admin, manager, finance, support
  // (priority #1). The per-page action gating (e.g. "only super_admin
  // can hard-delete") happens inside each page using scope checks; this
  // route gate's job is just to keep user-group out.
  if (!isOwnerGroup({ role: effectiveRole })) {
    // Distinguish "this account cannot open admin" from "this account is
    // currently wearing a hat that cannot". Both used to redirect saying "you
    // do not have permission", which is simply untrue for an admin viewing as
    // a member — and unanswerable, because nothing on screen connects the
    // refusal to the Test-as switcher that caused it.
    const wearingLesserHat = isOwnerGroup({ role: primaryRole });
    // The refusal is named on the landing page by RefusalNotice: which page,
    // which roles it needs, which role this account holds. `from` and `needs`
    // are what it needs to say that; `as` names the hat when the account
    // holds the role but is not wearing it.
    const params = new URLSearchParams({
      reason: wearingLesserHat ? 'persona_restricted' : 'insufficient_role',
      from: location.pathname || '/',
      needs: 'owner_group'
    });
    if (wearingLesserHat) params.set('as', effectiveRole);
    return <Navigate to={`/?${params.toString()}`} replace />;
  }

  // Staff, but not at the desk that works this page. The sidebar no longer
  // offers it (useDynamicCatalog resolves operator rows from the same desk
  // table), so arriving here means a typed or pasted URL — and without this
  // the page would render and fill with 403s, or worse, with data the gateway
  // still hands to any owner role. `false` only: a path the table does not
  // list (a detail page under no known console) is left to the owner-group
  // check above and the server.
  if (maySeeSurface(effectiveRole, location.pathname) === false) {
    const desks = desksForSurface(location.pathname) || [];
    // A surface no desk may open yet is refused by ROLE, which is the true
    // statement: the service behind it still asks for an admin.
    const byRole = desks.includes(AUTHORITY_ONLY) ? 'admin,super_admin' : desks.includes(SUPER_ONLY) ? 'super_admin' : null;
    const params = new URLSearchParams({
      reason: byRole ? 'insufficient_role' : 'desk_restricted',
      from: location.pathname || '/',
      needs: byRole || desks.filter((d) => d !== ANY_DESK).join(',')
    });
    return <Navigate to={`/admin/console?${params.toString()}`} replace />;
  }

  return children;
};
